import React from 'react';
import { ColorValue, StatusTone } from '../../types';
import { getStatusColor } from '../../utils/statusColors';
import './CircleGauge.css';

interface CircleGaugeProps {
  value: number;
  maxValue: number;
  targetValue?: number;
  targetMin?: number;
  targetMax?: number;
  colorStatus: ColorValue;
  statusTone?: StatusTone;
  /** When set, overrides status-based coloring with this single color */
  customColor?: string;
  units?: string;
  statusLabel?: string;
}

export const CircleGauge: React.FC<CircleGaugeProps> = ({
  value,
  maxValue,
  targetValue,
  targetMin,
  targetMax,
  colorStatus,
  customColor,
  units,
  statusLabel
}) => {
  const safeValue = value ?? 0;
  const safeMaxValue = maxValue;
  const safeTargetValue = targetValue;

  // Guard divide-by-zero. The displayed percentage is NOT capped, so a resident
  // above the max reads as e.g. 119% rather than a misleading 100%. The ring
  // itself is capped below, since a circle cannot be more than full.
  const rawPercentage = safeMaxValue && safeMaxValue > 0
    ? (safeValue / safeMaxValue) * 100
    : 0;
  const ringPercentage = Math.min(Math.max(rawPercentage, 0), 100);

  const formatNumber = (num: number): string => {
    // Add comma separators for big numbers
    const addCommas = (numStr: string): string => {
      return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };
    
    // Only show decimals if the number has meaningful decimal places
    if (num % 1 === 0) {
      return addCommas(num.toString()); // No decimals for whole numbers
    }
    return addCommas(num.toFixed(2)); // 2 decimals for decimal numbers
  };
  
  const getColor = () => getStatusColor((customColor as ColorValue) ?? colorStatus);

  // SVG circle parameters - responsive to container size
  const radius = 42; // Slightly smaller to account for stroke width
  const strokeWidth = 6; // Responsive stroke width
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - ringPercentage / 100);

  // Get goal status message
  const getGoalMessage = () => {
    if (rawPercentage >= 100) return 'Goal exceeded';
    if (rawPercentage >= 80) return 'Almost there';
    if (rawPercentage >= 60) return 'Good progress';
    if (rawPercentage >= 40) return 'Keep going';
    return 'Getting started';
  };

  return (
    <div className="circle-gauge">
      <div className="circle-gauge__container">
        <div className="circle-gauge__chart">
          <svg className="circle-gauge__svg" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="#f1f5f9"
              strokeWidth={strokeWidth}
            />
            
            {/* Progress circle */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={getColor()}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 50 50)"
              className="circle-gauge__progress"
            />
          </svg>
          
          <div className="circle-gauge__center">
            <div className="circle-gauge__value">
              {rawPercentage.toFixed(0)}
            </div>
            <div className="circle-gauge__units">
              %
            </div>
          </div>
        </div>
      </div>
      
      <div className="circle-gauge__details">
        <div className="circle-gauge__message">
          {statusLabel || getGoalMessage()}
        </div>
        <div className="circle-gauge__info">
          {formatNumber(safeValue)}{units ? ` ${units}` : ''} of {formatNumber(safeMaxValue)}{units ? ` ${units}` : ''}
        </div>
        {(targetMin != null || targetMax != null) ? (
          <div className="circle-gauge__target">
            Target:{' '}
            {targetMin != null && targetMax != null
              ? `${formatNumber(targetMin)}–${formatNumber(targetMax)}`
              : targetMin != null
              ? `≥ ${formatNumber(targetMin)}`
              : `≤ ${formatNumber(targetMax!)}`}
            {units ? ` ${units}` : ''}
          </div>
        ) : safeTargetValue != null && safeTargetValue !== safeMaxValue && (
          <div className="circle-gauge__target">
            Target: {formatNumber(safeTargetValue)}{units ? ` ${units}` : ''}
          </div>
        )}
      </div>
    </div>
  );
};
